/**
 * Account Detail Page - Detail overzicht van een specifiek account
 * Toont projecten, offertes, facturen en actiepunten
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  LinearProgress,
  IconButton,
  Button,
  TextField,
  Divider,
  List,
  ListItem,
  ListItemText,
  useTheme
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  Receipt as ReceiptIcon,
  Description as DescriptionIcon,
  FolderOpen as FolderIcon,
} from '@mui/icons-material';
import { getCached, setCached } from '../../utils/pageCache';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import AccountCrmSection from './AccountCrmSection';
import AccountPlanSection from './AccountPlanSection';
import AccountSatisfactionSection from './AccountSatisfactionSection';

// Format currency
const formatCurrency = (amount) => {
  // LET OP: de backend levert euro's (zelfde bron als het Sales Dashboard) —
  // hier stond /100 alsof het centen waren, waardoor alles 100x te klein toonde.
  const v = typeof amount === 'number' ? amount : 0;
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency', currency: 'EUR',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(v);
};

// Format percentage
const formatPercentage = (value, total) => {
  if (!total || total === 0) return '0%';
  return `${Math.round((value / total) * 100)}%`;
};

// Project Status Colors
const getProjectStatusColor = (status) => {
  switch (status) {
    case 1: return 'primary'; // Actief
    case 2: return 'success'; // Afgerond
    case 3: return 'default'; // Gearchiveerd
    case 8: return 'warning'; // Te factureren
    case 9: return 'info'; // Wachten op
    case 10: return 'error'; // On Hold
    default: return 'default';
  }
};

// Summary Card Component
const SummaryCard = ({ title, value, subtitle, icon: Icon, color }) => (
  <Card sx={{ height: '100%', bgcolor: `${color}10`, border: 'none', boxShadow: 'none' }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight={600} color={color}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="textSecondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Avatar sx={{ bgcolor: `${color}20`, color, width: 56, height: 56 }}>
          <Icon fontSize="large" />
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

export default function AccountDetail() {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  // Cache: bij terug-navigeren meteen de vorige data tonen (geen leeg laadscherm).
  const cacheKey = `account:${accountId}`;
  const cachedInit = getCached(cacheKey);
  const [account, setAccount] = useState(cachedInit?.data || null);
  const [loading, setLoading] = useState(!cachedInit);   // met cache: niet blokkeren
  const [error, setError] = useState(null);

  // Fetch account data (stale-while-revalidate)
  const fetchAccount = useCallback(async () => {
    const cached = getCached(cacheKey);
    if (!cached) setLoading(true);   // alleen blokkeren als er niks te tonen is
    setError(null);
    try {
      const response = await authFetch(`${BACKEND_URL}/Api/account/${accountId}`);
      if (!response.ok) {
        throw new Error('Account niet gevonden');
      }
      const data = await response.json();
      setAccount(data);
      setCached(cacheKey, data);
    } catch (err) {
      console.error('Error fetching account:', err);
      if (!cached) setError(err.message);   // met cache: fout stil (oude data blijft staan)
    } finally {
      setLoading(false);
    }
  }, [accountId, cacheKey]);

  useEffect(() => {
    fetchAccount();
  }, [fetchAccount]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/sales-dashboard')}
          sx={{ mb: 2 }}
        >
          Terug naar Dashboard
        </Button>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!account) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning">Account niet gevonden</Alert>
      </Container>
    );
  }

  const gripp = account.grippData || {};
  const totalRealized = (gripp.invoices?.totalAmount || 0) + (gripp.projects?.totalAmount || 0);
  const progressPercentage = account.budget2026
    ? Math.min((totalRealized / account.budget2026) * 100, 150)
    : 0;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/sales-dashboard')}
          sx={{ mb: 2 }}
        >
          Terug naar Dashboard
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          {account.logo ? (
            <Avatar
              src={account.logo}
              alt={account.name}
              sx={{ width: 80, height: 80 }}
              variant="rounded"
            />
          ) : (
            <Avatar sx={{ width: 80, height: 80, bgcolor: theme.palette.primary.main }} variant="rounded">
              <BusinessIcon sx={{ fontSize: 40 }} />
            </Avatar>
          )}
          <Box>
            <Typography variant="h4" fontWeight={600}>
              {account.name}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Budget 2026: {formatCurrency(account.budget2026)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Relatiebeheer: accountmanager, contact-ritme + meten, todo's */}
      <AccountCrmSection accountId={accountId} accountName={account.name} defaultAccountmanager={account.accountmanagerAirtable} />

      {/* Klanttevredenheid uit de evaluaties (#63) */}
      <AccountSatisfactionSection accountName={account.name} />

      {/* Accountplan (Klant Canvas) — geïmporteerd + bij te werken */}
      <AccountPlanSection accountId={accountId} />

      {/* Progress Bar */}
      <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" color="textSecondary">
            Voortgang naar budget
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {formatPercentage(totalRealized, account.budget2026)} ({formatCurrency(totalRealized)} / {formatCurrency(account.budget2026)})
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={Math.min(progressPercentage, 100)}
          sx={{
            height: 12,
            borderRadius: 6,
            bgcolor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              borderRadius: 6,
              bgcolor: progressPercentage >= 100 ? 'success.main' : 'primary.main',
            },
          }}
        />
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Gefactureerd"
            value={formatCurrency(gripp.invoices?.totalAmount || 0)}
            subtitle={`${gripp.invoices?.totalCount || 0} facturen`}
            icon={ReceiptIcon}
            color="#10b981"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Opdrachten"
            value={formatCurrency(gripp.projects?.totalAmount || 0)}
            subtitle={`${gripp.projects?.count || 0} projecten`}
            icon={FolderIcon}
            color="#3b82f6"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Open Offertes"
            value={formatCurrency(gripp.offers?.openTotal || 0)}
            subtitle={`${gripp.offers?.openCount || 0} offertes`}
            icon={DescriptionIcon}
            color="#f59e0b"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Nog te realiseren"
            value={formatCurrency(Math.max((account.budget2026 || 0) - totalRealized, 0))}
            subtitle="tot budget"
            icon={BusinessIcon}
            color="#6366f1"
          />
        </Grid>
      </Grid>

      {/* Projects Section */}
      <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          Projecten ({gripp.projects?.count || 0})
        </Typography>

        {gripp.projects?.items?.length > 0 ? (
          <List disablePadding>
            {gripp.projects.items.map((project, index) => (
              <React.Fragment key={project.id}>
                {index > 0 && <Divider />}
                {/* Dense: alles op één regel — nummer · naam · bedrag · status */}
                <ListItem sx={{ py: 0.6, px: 0, gap: 1 }}>
                  <Typography variant="body2" noWrap title={`${project.number} - ${project.name}`} sx={{ flex: 1, minWidth: 0 }}>
                    <b>{project.number}</b> - {project.name}
                  </Typography>
                  <Typography variant="body2" color="primary" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                    {formatCurrency(project.totalAmount)}
                  </Typography>
                  <Chip label={project.statusName || 'Onbekend'} size="small" color={getProjectStatusColor(project.status)} />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="textSecondary">
            Geen projecten gevonden
          </Typography>
        )}
      </Paper>

      {/* Offers Section */}
      <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          Offertes ({gripp.offers?.items?.length || 0})
        </Typography>

        {gripp.offers?.items?.length > 0 ? (
          <List disablePadding>
            {gripp.offers.items.map((offer, index) => (
              <React.Fragment key={offer.id}>
                {index > 0 && <Divider />}
                <ListItem sx={{ py: 2, px: 0 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body1" fontWeight={500}>
                          {offer.number} - {offer.subject}
                        </Typography>
                        <Chip
                          label={offer.phaseName || 'Onbekend'}
                          size="small"
                          color={offer.phase === 3 ? 'success' : offer.phase === 2 ? 'warning' : 'default'}
                        />
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" color="warning.main" fontWeight={600}>
                        {formatCurrency(offer.totalAmount)}
                      </Typography>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="textSecondary">
            Geen offertes gevonden
          </Typography>
        )}
      </Paper>

      {/* Invoices Section */}
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          Facturen ({gripp.invoices?.items?.length || 0})
        </Typography>

        {gripp.invoices?.items?.length > 0 ? (
          <List disablePadding>
            {gripp.invoices.items.map((invoice, index) => (
              <React.Fragment key={invoice.id}>
                {index > 0 && <Divider />}
                <ListItem sx={{ py: 2, px: 0 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body1" fontWeight={500}>
                          {invoice.number} - {invoice.subject}
                        </Typography>
                        <Chip
                          label={invoice.isPaid ? 'Betaald' : 'Open'}
                          size="small"
                          color={invoice.isPaid ? 'success' : 'warning'}
                        />
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" color="success.main" fontWeight={600}>
                        {formatCurrency(invoice.totalAmount)}
                      </Typography>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="textSecondary">
            Geen facturen gevonden
          </Typography>
        )}
      </Paper>
    </Container>
  );
}
